import type { ColumnSizingState } from '@tanstack/react-table';
import type { StrictColumnDef } from '../../../../components/tanstack-table/column';
import { caseCount, orderTotalCost, unitCount } from '../../../../data/order';
import { casesOf } from '../../../../data/order-item';
import type { SalesPeriodDays } from '../../../../data/order-settings';
import { caseDiscount, casesSold, volumeDiscount } from '../../../../data/product';
import { formatPercent, preciseCurrencyFormatter } from '../../../../helpers/format-currency';
import type { ProductOrderItem } from '../data/product-order-item';

type OrderSummaryHeaderProps = {
  items: ProductOrderItem[];
  columns: StrictColumnDef<ProductOrderItem>[];
  /** Column sizing from TanStackTable (keyed by column id) */
  columnSizing: ColumnSizingState;
};

/**
 * Summary header for regular order view.
 * Shows totals for Order Cases, Order Units, and Extended Cost.
 */
export function OrderSummaryHeader({ items, columns, columnSizing }: OrderSummaryHeaderProps) {
  const totalCasesCount = caseCount(items);
  const totalUnitsCount = unitCount(items);
  const totalCost = orderTotalCost(items);

  return (
    <>
      {columns.map((col) => {
        const width = columnSizing[col.id] ?? col.minSize ?? col.meta?.minWidth ?? 100;
        let content: React.ReactNode = null;

        switch (col.id) {
          case 'Order Cases':
            content = <div className="content-center text-center">{totalCasesCount}</div>;
            break;
          case 'Order Units':
            content = <div className="content-center text-center">{totalUnitsCount}</div>;
            break;
          case 'Extended Cost':
            content = (
              <div className="content-center text-center">
                {preciseCurrencyFormatter(totalCost)}
              </div>
            );
            break;
        }

        return (
          <div
            key={col.id}
            style={{ width, minWidth: width, maxWidth: width }}
            className="shrink-0 px-1 py-1 text-sm font-semibold text-gray-700"
          >
            {content}
          </div>
        );
      })}
    </>
  );
}

type BuyupSummaryHeaderProps = {
  items: ProductOrderItem[];
  columns: StrictColumnDef<ProductOrderItem>[];
  /** Column sizing from TanStackTable (keyed by column id) */
  columnSizing: ColumnSizingState;
  onAddAllDeals?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onRemoveAllDeals?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  hasDealSelection?: boolean;
};

/**
 * Summary header for buyup/deals view.
 * Shows Deal buttons, Order Cases, Extended Cost, Buyup Savings, Savings Potential, and Cases Sold sums.
 */
export function BuyupSummaryHeader({
  items,
  columns,
  columnSizing,
  onAddAllDeals,
  onRemoveAllDeals,
  hasDealSelection,
}: BuyupSummaryHeaderProps) {
  const totalCasesCount = caseCount(items);
  const totalCost = items.reduce(
    (sum, item) => sum + casesOf(item) * item.product.case_qty * item.product.last_cost,
    0
  );
  const savings = items.reduce((sum, item) => {
    if (item.product.pricing.volume_discounts) {
      return sum + casesOf(item) * volumeDiscount(item.product, casesOf(item));
    }

    return sum + casesOf(item) * caseDiscount(item.product);
  }, 0);
  const savingsPercent = savings / (totalCost + savings) || 0;

  const casesSoldSum = (salesPeriod: SalesPeriodDays) => {
    const sum = items.reduce((acc, item) => acc + casesSold(item.product, salesPeriod), 0);

    if (sum === 0) {
      return '-';
    }

    if (sum > 999) {
      return '>999';
    }

    return sum.toFixed(0);
  };

  return (
    <>
      {columns.map((col) => {
        const width = columnSizing[col.id] ?? col.minSize ?? col.meta?.minWidth ?? 100;
        let content: React.ReactNode = null;

        switch (col.id) {
          case 'Deal':
            if (hasDealSelection && onRemoveAllDeals) {
              content = (
                <div className="w-full flex justify-center text-center!">
                  <button
                    type="button"
                    className="text-xs text-red-500 hover:underline font-normal"
                    onClick={onRemoveAllDeals}
                  >
                    Clear
                  </button>
                </div>
              );
            } else if (onAddAllDeals) {
              content = (
                <div className="w-full flex justify-center text-center!">
                  <button
                    type="button"
                    className="text-xs text-blue-500 hover:underline font-normal"
                    onClick={onAddAllDeals}
                  >
                    Add All
                  </button>
                </div>
              );
            }

            break;
          case 'Order Cases':
            content = <div className="content-center text-center">{totalCasesCount}</div>;
            break;
          case 'Extended Cost':
            content = (
              <div className="content-center text-center">
                {preciseCurrencyFormatter(totalCost)}
              </div>
            );
            break;
          case 'Buyup Savings':
            content = (
              <div className="content-center text-center">{preciseCurrencyFormatter(savings)}</div>
            );
            break;
          case 'Savings Potential 90d':
            content = (
              <div
                className={`content-center text-center ${savingsPercent < 0 ? 'text-red-600' : 'text-green-600'}`}
              >
                {formatPercent(savingsPercent)}
              </div>
            );
            break;
          case '30d Cases Sold':
            content = (
              <div className="content-center text-center text-gray-500">{casesSoldSum(30)}</div>
            );
            break;
          case '60d Cases Sold':
            content = (
              <div className="content-center text-center text-gray-500">{casesSoldSum(60)}</div>
            );
            break;
          case '90d Cases Sold':
            content = (
              <div className="content-center text-center text-gray-500">{casesSoldSum(90)}</div>
            );
            break;
          case '180d Cases Sold':
            content = (
              <div className="content-center text-center text-gray-500">{casesSoldSum(180)}</div>
            );
            break;
          case '365d Cases Sold':
            content = (
              <div className="content-center text-center text-gray-500">{casesSoldSum(365)}</div>
            );
            break;
        }

        return (
          <div
            key={col.id}
            style={{ width, minWidth: width, maxWidth: width }}
            className="shrink-0 px-1 py-1 text-sm font-semibold text-gray-700"
          >
            {content}
          </div>
        );
      })}
    </>
  );
}
